import { useQuery } from "@tanstack/react-query";
import { defaultInstance } from "../api/httpClient";

// Business interface based on the API response structure
export interface Business {
  id: string;
  name: string;
  estate_id: string | null;
  description: string;
  phone_number: string;
  logo_url: string;
  background_img_url: string;
  schedule_mon: string[];
  schedule_tue: string[];
  schedule_wed: string[];
  schedule_thu: string[];
  schedule_fri: string[];
  schedule_sat: string[];
  schedule_sun: string[];
  user_types_ids: number[];
  business_type_id: number;
  img_gallery_url: string[];
  website_url: string | null;
  instagram_url: string | null;
  x_url: string | null;
  facebook_url: string | null;
  business_category_id: number;
  description_es: string;
  description_en: string | null;
  email: string | null;
  booking_id: number;
  is_active: boolean;
  show_in_app: boolean;
  show_gatekeeper: boolean;
  api_key: string | null;
  availability: any[];
  tenant_id: string;
  settings?: BusinessSettings | null;
  // SAM-1237: where the business is (internal = an estate group of the tenant, external = an address, virtual = none).
  location_type?: 'internal' | 'external' | 'virtual' | null;
  building_id?: number | null;
  address?: string | null;
  city?: string | null;
  zip_code?: string | null;
  country?: string | null;
}

/**
 * Flat key/value blob stored in `business.settings` JSONB.
 * Currently used for Cardnet integration credentials. Cardnet checkout is only
 * available for a product when its business has BOTH `merchant_id` and
 * `terminal_id` set here.
 */
export interface BusinessSettings {
  merchant_id?: string | null;
  terminal_id?: string | null;
  [key: string]: any;
}

export const businessHasCarnetConfigured = (settings?: BusinessSettings | null): boolean => {
  return !!(settings && settings.merchant_id && settings.terminal_id);
};

export interface BusinessResponse {
  data: Business[];
}

// Business Access Type interface
export interface BusinessAccessType {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BusinessAccessTypeResponse {
  data: BusinessAccessType[];
}

// Business Access Status interface (SAM-581: single source of truth for
// access-status labels, mirroring BusinessAccessType above).
export interface BusinessAccessStatus {
  id: number;
  name: string;
}

export interface BusinessAccessStatusResponse {
  data: BusinessAccessStatus[];
}

// Invitation Type interface (for residential)
export interface InvitationType {
  id: number;
  name: string;
  description: string;
  duration_limit_days: number;
  max_adults: number;
  max_children: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface InvitationTypeResponse {
  data: InvitationType[];
}

// Generic API Response types
type ApiResponse<T> = {
  data: T;
  message?: string;
}

type PaginatedApiResponse<T> = {
  data: T[];
  total_pages: number;
  page: number;
  total_elements: number;
}

// Hook to fetch all businesses
export const useBusinesses = () => {
  return useQuery<BusinessResponse, Error>({
    queryKey: ['businesses'],
    queryFn: async () => {
      const response = await defaultInstance<BusinessResponse>({
        url: '/business',
        method: 'GET'
      });
      return response.data;
    }
  });
};

// Hook to fetch business access types
export const useBusinessAccessTypes = () => {
  return useQuery<BusinessAccessTypeResponse, Error>({
    queryKey: ['business-access-types'],
    queryFn: async () => {
      const response = await defaultInstance<BusinessAccessTypeResponse>({
        url: '/business_access_type',
        method: 'GET'
      });
      return response.data;
    }
  });
};

// Hook to fetch business access statuses (SAM-581)
export const useBusinessAccessStatuses = () => {
  return useQuery<BusinessAccessStatusResponse, Error>({
    queryKey: ['business-access-statuses'],
    queryFn: async () => {
      const response = await defaultInstance<BusinessAccessStatusResponse>({
        url: '/business_access_status',
        method: 'GET'
      });
      return response.data;
    }
  });
};

// Hook to fetch a single business access type
export const useBusinessAccessType = (id: string) => {
  return useQuery<BusinessAccessType, Error>({
    queryKey: ['business-access-type', id],
    queryFn: async () => {
      const response = await defaultInstance<{ data: BusinessAccessType }>({
        url: `/business_access_type/${id}`,
        method: 'GET'
      });
      return response.data.data;
    },
    enabled: !!id
  });
};

// Hook to fetch invitation types (for residential)
export const useInvitationTypes = () => {
  return useQuery<InvitationTypeResponse, Error>({
    queryKey: ['invitation-types'],
    queryFn: async () => {
      const response = await defaultInstance<InvitationTypeResponse>({
        url: '/invitation_type',
        method: 'GET'
      });
      return response.data;
    }
  });
};

// Hook to fetch a single invitation type
export const useInvitationType = (id: string) => {
  return useQuery<InvitationType, Error>({
    queryKey: ['invitation-type', id],
    queryFn: async () => {
      const response = await defaultInstance<{ data: InvitationType }>({
        url: `/invitation_type/${id}`,
        method: 'GET'
      });
      return response.data.data;
    },
    enabled: !!id
  });
};

// Legacy estate access types (keeping for backward compatibility)
export const useEstateAccessType = (id: number) => {
  return useQuery({
    queryKey: ["estate-access-type", id],
    queryFn: async () => {
      try {
        const response = await defaultInstance<ApiResponse<InvitationType>>({
          method: 'GET',
          url: `/estate_access_type/${id}`
        });
        return response.data;
      } catch (error) {
        console.error("Error fetching estate access type:", error);
        throw error;
      }
    }
  });
};

export const useEstateAccessTypes = () => {
  return useQuery({
    queryKey: ["estate-access-types"],
    queryFn: async () => {
      try {
        const response = await defaultInstance<PaginatedApiResponse<InvitationType>>({
          method: 'GET',
          url: `/estate_access_type`
        });
        return response.data;
      } catch (error) {
        console.error("Error fetching estate access types:", error);
        throw error;
      }
    }
  });
}; 